// MissionPeriod component: Manages the mission search filters, results display, and detail view.
import MissionDisplay from 'components/Mission/MissionDisplay';
import Badge from 'components/badge/badge';
import {
  Building,
  Calendar,
  Filter,
  PersonIcon,
  ReferenceIcon,
  SaveIcon,
  SearchLoop,
  Signer,
} from '../../../assets/icons';
import Tooltip from '../../../components/tooltips/Tooltips';
import EmptyState from '../../../components/EmptyState/EmptyState';
import Button from '../../../components/button';
import PageForm from '../../../components/pageForm/PageForm';
import SubNavbar from '../../../components/SubNavbar/SubNavbar';
import StyledInput from '../../../components/styledInput/StyledInput';
import ThreeStateCheckbox from '../../../components/threeStateCheckbox/threeStateCheckbox';
import FilterDropdown from '../../../components/filter/FilterDropdown';
import BottomSheet from '../../../components/bottomSheet/BottomSheet';
import MediaQuery from 'react-responsive';
import { useMemo, useState } from 'react';
import { generateEmployeeColor } from '../../../helper/helper';
import useMissionPeriod from '../../../hooks/useMissionPeriod';

const MissionPeriod = () => {
  // State for mobile filter bottom sheet
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  
  // Temporary filter states for bottom sheet (apply on "Appliquer" button)
  const [tempSearch, setTempSearch] = useState('');
  const [tempIsMissionSearchAssigned, setTempIsMissionSearchAssigned] = useState(null);
  const [tempIsMissionSearchSourcing, setTempIsMissionSearchSourcing] = useState(null);

  // Destructure values and actions from the custom hook
  const {
    search,
    setSearch,
    searchMissionResult,
    searchMissionResultWaiting,
    isMissionSearchAssigned,
    setIsMissionSearchAssigned,
    isMissionSearchSourcing,
    setIsMissionSearchSourcing,
    typeContract,
    updateTypeContract,
    departments,
    updateDepartment,
    missionPeriod,
    updateMissionPeriod,
    missionStatus,
    updateMissionStatus,
    employees,
    employeesSearch,
    updateEmployee,
    updateEmployeeSearch,
    openCompanyInfo,
    openTempWorkerInfo,
    handleDeleteMission,
    handleCancelMission,
    handleSelectMission,
    selectedMissions,
    shouldShowTrashButton,
    updateEmployeeForMission,
    currentPage,
    setCurrentPage,
    totalPages,
    countPerPage,
    countTotal,
    editMission,
    setEditMission,
    hoveredCompanyItems,
    setHoveredCompanyItems,
    setSearchMissionResult,
    hasFilterChanges,
    getHasFilterChanges,
    saveDefaultFilters,
  } = useMissionPeriod();

  const mobileFilterOverrides = useMemo(
    () =>
      isFilterSheetOpen
        ? {
            isMissionSearchAssigned: tempIsMissionSearchAssigned,
            isMissionSearchSourcing: tempIsMissionSearchSourcing,
          }
        : {},
    [
      isFilterSheetOpen,
      tempIsMissionSearchAssigned,
      tempIsMissionSearchSourcing,
    ]
  );

  const mobileHasFilterChanges = getHasFilterChanges(mobileFilterOverrides);

  const handleMobileSaveFilters = () => {
    saveDefaultFilters(mobileFilterOverrides);
  };

  // Open filter sheet and sync temporary states
  const openFilterSheet = () => {
    setTempSearch(search);
    setTempIsMissionSearchAssigned(isMissionSearchAssigned);
    setTempIsMissionSearchSourcing(isMissionSearchSourcing);
    setIsFilterSheetOpen(true);
  };

  // Apply filters from bottom sheet
  const applyFilters = () => {
    setSearch(tempSearch);
    setIsMissionSearchAssigned(tempIsMissionSearchAssigned);
    setIsMissionSearchSourcing(tempIsMissionSearchSourcing);
    setIsFilterSheetOpen(false);
  };

  return (
    <PageForm>
      <>
        <SubNavbar>
          {/* Filter and search bar */}
          <div className="flex justify-between items-center text-sm w-full">
            {/* Desktop: All filters visible */}
            <MediaQuery minWidth={768}>
              <div className="flex items-center gap-2">
                {/* Checkbox for mission assignment filter */}
                <div className="border rounded px-4 h-10 flex items-center hover:bg-gray-100 transition-colors">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <ThreeStateCheckbox
                      checked={isMissionSearchAssigned}
                      onChange={setIsMissionSearchAssigned}
                    />
                    <div className="whitespace-nowrap">Mission attribuée</div>
                  </label>
                </div>
                {/* Checkbox for sourcing filter */}
                <div className="border rounded px-4 h-10 flex items-center hover:bg-gray-100 transition-colors">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <ThreeStateCheckbox
                      checked={isMissionSearchSourcing}
                      onChange={setIsMissionSearchSourcing}
                    />
                    <div className="whitespace-nowrap">Sourcing</div>
                  </label>
                </div>
                {/* Dropdown for contract types */}
                <FilterDropdown
                  maxWidth="max-w-[155px]"
                  value="Contrat"
                  list={typeContract.map((contract) => ({
                    value: contract.type,
                    label: contract.name,
                    checked: contract.checked,
                  }))}
                  onChange={updateTypeContract}
                  type="selectMultipleCheckbox"
                  classNameValue="text-sm h-10 px-4 flex items-center justify-start"
                  additionalClassName="h-10"
                />
                {/* Dropdown for departments */}
                <FilterDropdown
                  maxWidth="max-w-[175px]"
                  value="Département"
                  list={departments.map((department) => ({
                    value: department.name,
                    label: department.name,
                    checked: department.checked,
                  }))}
                  onChange={updateDepartment}
                  type="selectMultipleCheckbox"
                  classNameValue="text-sm h-10 px-4 flex items-center justify-start"
                  additionalClassName="h-10"
                />
                {/* Dropdown for mission period (temporal) */}
                <FilterDropdown
                  maxWidth="max-w-[155px]"
                  value={
                    missionPeriod.filter((p) => p.checked).length > 0
                      ? missionPeriod
                          .filter((p) => p.checked)
                          .map((p) => p.name)
                          .join(', ')
                      : 'Période'
                  }
                  list={missionPeriod.map((p) => ({
                    value: p.type,
                    label: p.name,
                    checked: p.checked,
                  }))}
                  onChange={updateMissionPeriod}
                  type="selectMultipleCheckbox"
                  classNameValue="text-sm h-10 px-4 flex items-center justify-start"
                  additionalClassName="h-10"
                />
                {/* Dropdown for mission status (action) */}
                <FilterDropdown
                  maxWidth="max-w-[170px]"
                  value={
                    missionStatus.filter((status) => status.checked).length > 0
                      ? missionStatus
                          .filter((status) => status.checked)
                          .map((status) => status.name)
                          .join(', ')
                      : 'Statut'
                  }
                  list={missionStatus.map((status) => ({
                    value: status.type,
                    label: status.name,
                    checked: status.checked,
                  }))}
                  onChange={updateMissionStatus}
                  type="selectMultipleCheckbox"
                  classNameValue="text-sm h-10 px-4 flex items-center justify-start"
                  additionalClassName="h-10"
                />
                {/* Dropdown for employee assignment */}
                <FilterDropdown
                  maxWidth="max-w-[180px]"
                  value={
                    employeesSearch.filter((employee) => employee.checked)
                      .length > 0
                      ? employeesSearch
                          .filter((employee) => employee.checked)
                          .map(
                            (employee) =>
                              `${employee.firstname} ${employee.lastname}`
                          )
                          .join(', ')
                      : 'Assigné'
                  }
                  list={employeesSearch.map((employee) => ({
                    value: employee._id,
                    label: (
                      <div className="flex gap-1.5 items-center">
                        {employee._id !== 'unassign' && (
                          <div
                            className="rounded-full w-[24px] h-[24px] flex items-center justify-center text-[10px] text-white"
                            style={{
                              backgroundColor: generateEmployeeColor(
                                employee.firstname,
                                employee.lastname
                              ),
                            }}
                          >
                            {`${
                              employee.firstname?.charAt(0).toUpperCase() || ''
                            }${employee.lastname?.charAt(0).toUpperCase() || ''}`}
                          </div>
                        )}
                        <div>
                          {employee.firstname} {employee.lastname}
                        </div>
                      </div>
                    ),
                    checked: employee.checked,
                  }))}
                  onChange={updateEmployeeSearch}
                  type="selectMultipleCheckbox"
                  additionalClassNameDropDown="text-gray-900"
                  classNameValue="text-sm h-10 px-4 flex items-center justify-start"
                />
                <Tooltip text="Sauvegarder les filtres" placement="bottom" disabled={!hasFilterChanges}>
                  <Button
                    color={hasFilterChanges ? 'SECONDARY' : 'DISABLED'}
                    size="SMI"
                    disabled={!hasFilterChanges}
                    onClick={saveDefaultFilters}
                  >
                    <SaveIcon wh={20} color={hasFilterChanges ? '#374151' : '#9CA3AF'} />
                  </Button>
                </Tooltip>
              </div>
              {/* Global search input */}
              <div className="flex items-center ml-[12px]">
                <div className="flex-1 max-w-[400px]">
                  <StyledInput
                    type="text"
                    placeholder="Rechercher..."
                    imgFront={<SearchLoop wh={16} color="#6B7280" />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </MediaQuery>

            {/* Mobile: Filter button */}
            <MediaQuery maxWidth={767}>
              <Button
                color={'SECONDARY'}
                size={'SM'}
                onClick={openFilterSheet}
              >
                <Filter wh={16} color="#374151" />
                <span>Filtrer</span>
              </Button>
            </MediaQuery>
          </div>
        </SubNavbar>

        {/* Mobile: BottomSheet for filters */}
        <BottomSheet
          isOpen={isFilterSheetOpen}
          onClose={() => setIsFilterSheetOpen(false)}
          title="Filtres"
        >
          <div className="flex flex-col gap-4">
            {/* Search input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rechercher</label>
              <StyledInput
                type="text"
                placeholder="Rechercher..."
                imgFront={<SearchLoop wh={16} color="#6B7280" />}
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
              />
            </div>

            {/* Mission attribuée checkbox */}
            <div className="border rounded px-4 h-10 flex items-center hover:bg-gray-100 transition-colors">
              <label className="flex items-center gap-2 cursor-pointer">
                <ThreeStateCheckbox
                  checked={tempIsMissionSearchAssigned}
                  onChange={setTempIsMissionSearchAssigned}
                />
                <div className="whitespace-nowrap">Mission attribuée</div>
              </label>
            </div>

            {/* Sourcing checkbox */}
            <div className="border rounded px-4 h-10 flex items-center hover:bg-gray-100 transition-colors">
              <label className="flex items-center gap-2 cursor-pointer">
                <ThreeStateCheckbox
                  checked={tempIsMissionSearchSourcing}
                  onChange={setTempIsMissionSearchSourcing}
                />
                <div className="whitespace-nowrap">Sourcing</div>
              </label>
            </div>

            {/* Contract types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contrat</label>
              <FilterDropdown
                maxWidth="w-full"
                value="Sélectionner"
                list={typeContract.map((contract) => ({
                  value: contract.type,
                  label: contract.name,
                  checked: contract.checked,
                }))}
                onChange={updateTypeContract}
                type="selectMultipleCheckbox"
                classNameValue="text-sm h-10 px-4 flex items-center justify-start"
              />
            </div>

            {/* Departments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
              <FilterDropdown
                maxWidth="w-full"
                value="Sélectionner"
                list={departments.map((department) => ({
                  value: department.name,
                  label: department.name,
                  checked: department.checked,
                }))}
                onChange={updateDepartment}
                type="selectMultipleCheckbox"
                classNameValue="text-sm h-10 px-4 flex items-center justify-start"
              />
            </div>

            {/* Mission period (temporal) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
              <FilterDropdown
                maxWidth="w-full"
                value={
                  missionPeriod.filter((p) => p.checked).length > 0
                    ? missionPeriod
                        .filter((p) => p.checked)
                        .map((p) => p.name)
                        .join(', ')
                    : 'Sélectionner'
                }
                list={missionPeriod.map((p) => ({
                  value: p.type,
                  label: p.name,
                  checked: p.checked,
                }))}
                onChange={updateMissionPeriod}
                type="selectMultipleCheckbox"
                classNameValue="text-sm h-10 px-4 flex items-center justify-start"
              />
            </div>

            {/* Mission status (action) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <FilterDropdown
                maxWidth="w-full"
                value={
                  missionStatus.filter((status) => status.checked).length > 0
                    ? missionStatus
                        .filter((status) => status.checked)
                        .map((status) => status.name)
                        .join(', ')
                    : 'Sélectionner'
                }
                list={missionStatus.map((status) => ({
                  value: status.type,
                  label: status.name,
                  checked: status.checked,
                }))}
                onChange={updateMissionStatus}
                type="selectMultipleCheckbox"
                classNameValue="text-sm h-10 px-4 flex items-center justify-start"
              />
            </div>

            {/* Employee assignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigné à</label>
              <FilterDropdown
                maxWidth="w-full"
                value={
                  employeesSearch.filter((employee) => employee.checked)
                    .length > 0
                    ? employeesSearch
                        .filter((employee) => employee.checked)
                        .map(
                          (employee) =>
                            `${employee.firstname} ${employee.lastname}`
                        )
                        .join(', ')
                    : 'Sélectionner'
                }
                list={employeesSearch.map((employee) => ({
                  value: employee._id,
                  label: (
                    <div className="flex gap-1.5 items-center">
                      {employee._id !== 'unassign' && (
                        <div
                          className="rounded-full shrink-0 w-[24px] h-[24px] flex items-center justify-center text-[10px] text-white"
                          style={{
                            backgroundColor: generateEmployeeColor(
                              employee.firstname,
                              employee.lastname
                            ),
                          }}
                        >
                          {`${
                            employee.firstname?.charAt(0).toUpperCase() || ''
                          }${employee.lastname?.charAt(0).toUpperCase() || ''}`}
                        </div>
                      )}
                      <div>
                        {employee.firstname} {employee.lastname}
                      </div>
                    </div>
                  ),
                  checked: employee.checked,
                }))}
                onChange={updateEmployeeSearch}
                type="selectMultipleCheckbox"
                additionalClassNameDropDown="text-gray-900"
                classNameValue="text-sm h-10 px-4 flex items-center justify-start"
              />
            </div>

            <Button
              color={mobileHasFilterChanges ? 'SECONDARY' : 'DISABLED'}
              size={'FULL'}
              disabled={!mobileHasFilterChanges}
              onClick={handleMobileSaveFilters}
            >
              <SaveIcon wh={20} color={mobileHasFilterChanges ? '#374151' : '#9CA3AF'} />
              Sauvegarder les filtres
            </Button>

            {/* Apply button */}
            <Button
              color={'PRIMARY'}
              size={'FULL'}
              onClick={applyFilters}
            >
              Appliquer
            </Button>
          </div>
        </BottomSheet>
        <div className="flex flex-col w-full h-full overflow-hidden mission-container">
        {/* Results and details display section */}
          <div className="flex flex-1 w-full h-full gap-3 overflow-y-auto overflow-x-hidden p-3 mission-container-body">
          {searchMissionResultWaiting ? (
            // Display loading state with animated placeholders
            <div className="flex-1 h-full w-full lg:w-[555px] lg:max-w-[555px] animate-pulse">
              <div className="flex flex-col gap-4 max-h-full w-full h-full">
                <div className="flex flex-col gap-3 overflow-y-auto h-full">
                  {[1, 2, 3, 4, 5].map((i, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-1.5 border border-gray-100 rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                    >
                      <div className="flex gap-1.5 justify-between items-center">
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <div className="truncate">
                            <div className="h-4 bg-gray-200 rounded w-56"></div>
                          </div>
                          <div>
                            <div className="h-4 bg-gray-200 rounded w-8"></div>
                          </div>
                        </div>
                        <div className="flex gap-1 items-center text-sm">
                          <div className="px-1.5">
                            <div className="h-6 bg-gray-200 rounded w-6"></div>
                          </div>
                          <Button size="SMI" color="DEFAULT">
                            <div className="h-6 bg-gray-200 rounded w-6"></div>
                          </Button>
                          <Button size="SMI" color="DEFAULT">
                            <div className="h-6 bg-gray-200 rounded w-6"></div>
                          </Button>
                          <Button size="SMI" color="DEFAULT">
                            <div className="h-6 bg-gray-200 rounded w-6"></div>
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm flex items-center justify-between gap-4">
                        <div className="flex flex-col gap-0.5 text-gray-700 w-1/2">
                          <div className="flex gap-1 items-center">
                            <div>
                              <Calendar wh={18} color="#374151" />
                            </div>
                            <div className="h-4 bg-gray-200 rounded w-56"></div>
                          </div>
                          <div className="flex gap-1 items-center">
                            <div>
                              <Building wh={18} color="#374151" />
                            </div>
                            <div className="flex gap-1">
                              <div className="h-4 bg-gray-200 rounded w-56"></div>
                            </div>
                          </div>
                          <div className="flex gap-1 items-center">
                            <div>
                              <Signer wh={18} color="#374151" />
                            </div>
                            <div className="h-4 bg-gray-200 rounded w-56"></div>
                          </div>
                          <div className="flex gap-1 items-center">
                            <div>
                              <ReferenceIcon wh={18} color="#374151" />
                            </div>
                            <div className="h-4 bg-gray-200 rounded w-56"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : searchMissionResult.length > 0 ? (
            // Display the mission results list and detail panel
            <MissionDisplay
              searchMissionResult={searchMissionResult}
              editMission={editMission}
              setEditMission={setEditMission}
              selectedMissions={selectedMissions}
              employees={employees}
              hoveredCompanyItems={hoveredCompanyItems}
              setHoveredCompanyItems={setHoveredCompanyItems}
              openCompanyInfo={openCompanyInfo}
              openTempWorkerInfo={openTempWorkerInfo}
              handleSelectMission={handleSelectMission}
              shouldShowTrashButton={shouldShowTrashButton}
              handleDeleteMission={handleDeleteMission}
              handleCancelMission={handleCancelMission}
              updateEmployeeForMission={updateEmployeeForMission}
              currentPage={currentPage}
              countPerPage={countPerPage}
              countTotal={countTotal}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              setSearchMissionResult={setSearchMissionResult}
            />
          ) : (
            // Display an empty state when no results are found
            <EmptyState type="mission_search" />
          )}

          {/* Display a fixed footer for bulk mission assignment if any mission is selected */}
          {selectedMissions.length > 0 && (
            <div className="fixed bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white p-1.5 rounded">
              <div className="flex items-center text-sm h-10">
                <div className="flex gap-1 items-center py-2 px-3">
                  <div className="w-fit">
                    <Badge type="WARNING">{selectedMissions.length}</Badge>
                  </div>
                  sélectionnée{selectedMissions.length > 1 ? 's' : ''}
                </div>
                <div className="w-0.5 h-1/2 self-center bg-white" />
                <div className="flex gap-1 items-center">
                  <StyledInput
                    type="selectMultipleRadio"
                    value={
                      <div className="flex gap-1">
                        <PersonIcon wh={20} color="#FFF" />
                        <div className="text-white">Attribuer à</div>
                      </div>
                    }
                    list={employees.map((employee) => ({
                      value: employee._id,
                      label: (
                        <div className="flex gap-1.5 items-center">
                          {employee._id !== 'unassign' && (
                            <div
                              className="rounded-full shrink-0 w-[24px] h-[24px] flex items-center justify-center text-[10px] text-white"
                              style={{
                                backgroundColor: generateEmployeeColor(
                                  employee.firstname,
                                  employee.lastname
                                ),
                              }}
                            >
                              {`${
                                employee.firstname?.charAt(0).toUpperCase() ||
                                ''
                              }${
                                employee.lastname?.charAt(0).toUpperCase() || ''
                              }`}
                            </div>
                          )}
                          <div>
                            {employee.firstname} {employee.lastname}
                          </div>
                        </div>
                      ),
                      checked: employee.checked,
                    }))}
                    className={'rounded hover:bg-gray-900'}
                    additionalClassNameDropDown={'text-gray-900'}
                    showChevron={false}
                    showCheck={false}
                    onChange={updateEmployee}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    </PageForm>
  );
};

export default MissionPeriod;
